import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getClient, getClientSummary } from '../services/api';
import {
  ArrowLeft, Building2, MapPin, Phone, Mail, FileText,
  Users, Briefcase, CreditCard, Star
} from 'lucide-react';

const statusColor = (s) =>
  s === 'active' ? 'bg-green-50 text-green-700' :
  s === 'inactive' ? 'bg-gray-50 text-gray-500' :
  'bg-red-50 text-red-600';

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between py-2 border-b border-gray-50">
    <span className="text-sm text-gray-400">{label}</span>
    <span className="text-sm font-medium text-gray-800">{value || '—'}</span>
  </div>
);

const ClientDetail = () => {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');

  const { data: client, isLoading } = useQuery({
    queryKey: ['client', id],
    queryFn : () => getClient(id).then(r => r.data.data)
  });

  const { data: summary } = useQuery({
    queryKey: ['client-summary', id],
    queryFn : () => getClientSummary(id).then(r => r.data.data)
  });

  if (isLoading) return (
    <div className="flex items-center justify-center h-64 text-gray-400">
      Loading client profile...
    </div>
  );

  if (!client) return (
    <div className="text-center py-12 text-gray-400">Client not found</div>
  );

  const tabs = [
    { id: 'overview',  label: 'Overview' },
    { id: 'contacts',  label: 'Contacts' },
    { id: 'rates',     label: 'Rate Cards' },
    { id: 'activity',  label: 'Recent Activity' }
  ];

  return (
    <div className="space-y-5">
      {/* Back Button */}
      <button onClick={() => navigate('/clients')}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft size={16} />
        Back to Clients
      </button>

      {/* Profile Header */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-start gap-5">
          <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center flex-shrink-0">
            <span className="text-blue-600 text-2xl font-bold">
              {client.company_name?.charAt(0)}
            </span>
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-xl font-semibold text-gray-900">{client.company_name}</h1>
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusColor(client.status)}`}>
                {client.status}
              </span>
            </div>

            <p className="text-sm text-gray-400 mt-1">
              {client.gstin || 'No GSTIN'} · {client.industry || 'Industry not set'}
            </p>

            <div className="flex flex-wrap gap-4 mt-3">
              {client.city && (
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                  <MapPin size={14} className="text-gray-400" />
                  {client.city}, {client.state}
                </div>
              )}
              {client.website && (
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                  <FileText size={14} className="text-gray-400" />
                  {client.website}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-4 flex-shrink-0">
            <div className="text-center">
              <p className="text-2xl font-semibold text-gray-900">{client._count?.deployments || 0}</p>
              <p className="text-xs text-gray-400">Deployed</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-semibold text-gray-900">{client._count?.job_requisitions || 0}</p>
              <p className="text-xs text-gray-400">Requisitions</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-semibold text-gray-900">{client._count?.invoices || 0}</p>
              <p className="text-xs text-gray-400">Invoices</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {tabs.map(t => (
          <button key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition
              ${tab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ── */}
      {tab === 'overview' && (
        <div className="grid grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Building2 size={16} className="text-blue-600" />
              Company Details
            </h3>
            <InfoRow label="Company Name"  value={client.company_name} />
            <InfoRow label="Trade Name"    value={client.trade_name} />
            <InfoRow label="Industry"      value={client.industry} />
            <InfoRow label="Company Size"  value={client.company_size} />
            <InfoRow label="GSTIN"         value={client.gstin} />
            <InfoRow label="PAN"           value={client.pan} />
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <MapPin size={16} className="text-red-500" />
              Address & Billing
            </h3>
            <InfoRow label="Address"          value={client.address} />
            <InfoRow label="City"             value={client.city} />
            <InfoRow label="State"            value={client.state} />
            <InfoRow label="Pincode"          value={client.pincode} />
            <InfoRow label="Billing Address"  value={client.billing_address} />
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <CreditCard size={16} className="text-green-600" />
              Finance
            </h3>
            <InfoRow label="Credit Period"  value={client.credit_period ? `${client.credit_period} days` : null} />
            <InfoRow label="Credit Limit"   value={client.credit_limit ? `₹${Number(client.credit_limit).toLocaleString('en-IN')}` : null} />
            <InfoRow label="Total Invoiced" value={summary?.invoiceSummary?.total_amount ? `₹${Number(summary.invoiceSummary.total_amount).toLocaleString('en-IN')}` : null} />
            <InfoRow label="Outstanding"    value={summary?.invoiceSummary?.outstanding_amount ? `₹${Number(summary.invoiceSummary.outstanding_amount).toLocaleString('en-IN')}` : null} />
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Briefcase size={16} className="text-amber-600" />
              Deployment Status
            </h3>
            {summary?.deployments?.length === 0 || !summary?.deployments ? (
              <div className="text-center py-6 text-gray-400 text-sm">No deployment data</div>
            ) : (
              summary.deployments.map(d => (
                <InfoRow key={d.status} label={d.status?.replace('_', ' ')} value={d._count} />
              ))
            )}
            <InfoRow label="Open Job Requisitions" value={summary?.openJobs} />
          </div>
        </div>
      )}

      {/* ── CONTACTS TAB ── */}
      {tab === 'contacts' && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Users size={16} className="text-blue-600" />
            Client Contacts
          </h3>
          {client.client_contacts?.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">No contacts added yet</div>
          ) : (
            <div className="space-y-3">
              {client.client_contacts?.map(c => (
                <div key={c.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{c.name}</p>
                    <p className="text-xs text-gray-400">{c.designation} {c.department ? `· ${c.department}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    {c.phone && (
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Phone size={13} className="text-gray-400" />
                        {c.phone}
                      </div>
                    )}
                    {c.email && (
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Mail size={13} className="text-gray-400" />
                        {c.email}
                      </div>
                    )}
                    {c.is_primary && (
                      <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full flex items-center gap-1">
                        <Star size={10} /> Primary
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── RATE CARDS TAB ── */}
      {tab === 'rates' && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <CreditCard size={16} className="text-green-600" />
            Rate Cards
          </h3>
          {client.client_rate_cards?.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">No active rate cards</div>
          ) : (
            <div className="space-y-3">
              {client.client_rate_cards?.map(rc => (
                <div key={rc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div>
                    <p className="text-sm font-medium text-gray-900 capitalize">
                      {rc.worker_category?.replace('_', ' ')} {rc.skill_level ? `· ${rc.skill_level}` : ''}
                    </p>
                    <p className="text-xs text-gray-400">
                      Effective from {rc.effective_from?.slice(0, 10)}
                    </p>
                  </div>
                  <div className="text-right text-sm">
                    <p className="text-gray-600">Pay: ₹{Number(rc.pay_rate).toLocaleString('en-IN')}/{rc.rate_unit}</p>
                    <p className="text-gray-900 font-medium">Bill: ₹{Number(rc.bill_rate).toLocaleString('en-IN')}/{rc.rate_unit}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── RECENT ACTIVITY TAB ── */}
      {tab === 'activity' && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Briefcase size={16} className="text-purple-600" />
            Recent Deployments
          </h3>
          {summary?.recentActivity?.length === 0 || !summary?.recentActivity ? (
            <div className="text-center py-8 text-gray-400 text-sm">No recent activity</div>
          ) : (
            <div className="space-y-3">
              {summary.recentActivity.map(d => (
                <div key={d.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {d.candidates?.first_name} {d.candidates?.last_name}
                    </p>
                    <p className="text-xs text-gray-400">{d.deployment_no}</p>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusColor(d.status)}`}>
                    {d.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientDetail;
